import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { TopBar } from './TopBar';
import { SideNav } from './SideNav';
import { BrandGlyph } from './BrandGlyph';
import { Inspector } from '@/components/common/Inspector';
import { CommandPalette } from '@/components/common/CommandPalette';
import { ConsoleDock } from '@/features/console/ConsoleDock';
import { useCodexStore } from '@/stores/codexStore';
import { useHydrated } from '@/stores/selectors';
import { useFocus } from '@/lib/useFocus';
import { useCanonMirror } from '@/engine/useCanonMirror';
import { Starfield } from '@/components/hud/Starfield';

/** 앱 셸: 상단바 + 좌측 내비 + 콘텐츠(Outlet). 부팅 시 Dexie에서 hydrate. */
export function AppShell() {
  const hydrated = useHydrated();
  const [focus] = useFocus();
  const started = useRef(false);
  useCanonMirror(); // K0: canon 변경 → 에이전트 미러 동기화(Tauri 한정, idle 2s)

  // 캐논 로드 (StrictMode 이중 호출 방지)
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void useCodexStore.getState().hydrate();
  }, []);

  // 전역 undo/redo (⌘Z / ⌘⇧Z)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z') {
        const target = e.target as HTMLElement | null;
        // 에디터/입력 중에는 기본 undo 양보
        if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;
        if (target?.isContentEditable) return;
        e.preventDefault();
        if (e.shiftKey) useCodexStore.getState().redo();
        else useCodexStore.getState().undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    // min-h-dvh: 모바일 브라우저 툴바가 펼쳐진(100vh>100dvh) 동안에도 셸이 화면 밖으로 남지 않게.
    // 좌우 padding: viewport-fit=cover 노치 기기(가로)에서 셸이 안전영역을 침범하지 않게 — 세로 폰은 0.
    <div
      className="flex min-h-dvh flex-col"
      style={{ paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)' }}
    >
      <Starfield />
      <TopBar />
      <div className="flex flex-1 flex-col md:flex-row">
        <SideNav />
        <main className="min-w-0 flex-1">{hydrated ? <Outlet /> : <BootLoading />}</main>
        {hydrated && focus && <Inspector id={focus} />}
      </div>
      <CommandPalette />
      <ConsoleDock />
    </div>
  );
}

function BootLoading() {
  return (
    <div className="flex h-[60vh] flex-col items-center justify-center gap-5">
      <BrandGlyph />
      <p className="label-mono animate-pulse">캐논 로딩…</p>
    </div>
  );
}
